import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { Suspense, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Campaigns } from '@/components/pages/channels/campaigns';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useAuthContext } from '@/contexts/auth-context';
import {
  useOpeningContext,
  useOpeningDetails,
} from '@/contexts/opening-context';
import { useLocalDateRanges } from '@/hooks/use-local-date-ranges';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { useWxCopilotSourceContext } from '@/hooks/use-wx-copilot-source-context';
import { sourceChannelContext } from '@/utils/source-copilot-context';
import { useShowHiringGoalEmptyState } from '@/components/pages/channels/use-show-hiring-goal-empty-state';
import { queryKeys } from '@/query-keys';

import { useChannelColumns } from './campaigns/columns';
import { JobPostingCard } from './job-posting-card';

function ChannelsSectionContent() {
  const openingSlug = useOpeningSlug();
  const { hiringGoalId, startDate, endDate, filterRange, displayPrefs } =
    useOpeningContext();
  const { canPostToJobBoards } = useAuthContext();

  // Kick this fetch off BEFORE `useOpeningDetails` so it fires in parallel
  // with the funnel-details API instead of waiting for the suspense to
  // resolve below.
  const { data, isError } = useSuspenseQuery({
    queryKey: [
      ...queryKeys.openingDetails.channels.list,
      openingSlug,
      hiringGoalId,
      displayPrefs.showAggregateHires,
      displayPrefs.showAggregateSpend,
      startDate,
      endDate,
      filterRange,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsSourcingChannels(
        openingSlug,
        hiringGoalId,
        displayPrefs.showAggregateHires,
        displayPrefs.showAggregateSpend,
        startDate,
        endDate,
        filterRange,
      ),
    // Must match the staleTime in `useLastAutomationChannel`, which declares
    // this same query key to kick the fetch off early (see its comment).
    // Without a shared staleTime, this mount sees the just-fetched data as
    // stale and fires a redundant duplicate request.
    staleTime: 10 * 1000, // 10 seconds
  });

  if (isError) {
    throw new Error('Error fetching channels');
  }

  const {
    opening: { postingIneligibilityReason, timeZone: openingTimeZone },
    hiringGoal,
  } = useOpeningDetails();
  const timeZone = useLocalDateRanges() ? openingTimeZone : undefined;

  const showHiringGoalEmptyState = useShowHiringGoalEmptyState(hiringGoal);

  const showJobPostingCard =
    canPostToJobBoards && postingIneligibilityReason === null;

  const columns = useChannelColumns({
    brand: data.brand_slug ?? 'no_brand',
    location: data.location_id,
    funnel: data.opening_id,
  });

  const campaigns = useMemo(() => data.active_channels, [data]);

  const activeCampaigns = useMemo(
    () =>
      campaigns.filter(
        campaign =>
          campaign.status === 'active' ||
          campaign.status === 'stopped' ||
          campaign.status === 'in_progress',
      ),
    [campaigns],
  );

  const isCurrentHiringGoal = hiringGoal?.is_current ?? false;

  const ongoingCampaigns = useMemo(() => {
    const other =
      isCurrentHiringGoal && data.other_channel ? [data.other_channel] : [];
    return [...activeCampaigns, ...other];
  }, [activeCampaigns, data.other_channel, isCurrentHiringGoal]);

  const scheduledCampaigns = useMemo(
    () => campaigns.filter(campaign => campaign.status === 'scheduled'),
    [campaigns],
  );

  const completedCampaigns = useMemo(() => {
    const filteredCompleted = campaigns.filter(
      campaign =>
        campaign.status === 'completed' || campaign.status === 'failed',
    );

    const other =
      !isCurrentHiringGoal && data.other_channel ? [data.other_channel] : [];
    return [...filteredCompleted, ...other];
  }, [campaigns, isCurrentHiringGoal, data.other_channel]);

  useWxCopilotSourceContext('channels', {
    sourceChannel: showHiringGoalEmptyState
      ? undefined
      : [...ongoingCampaigns, ...scheduledCampaigns, ...completedCampaigns]
          .slice(0, 50)
          .map(sourceChannelContext),
  });

  if (showHiringGoalEmptyState) return null;

  return (
    <>
      {ongoingCampaigns.length > 0 && (
        <>
          <Typography as="h2" variant="caption">
            <FormattedMessage
              defaultMessage="Active campaigns"
              description="Section: live channel campaigns for this opening"
            />
          </Typography>
          <Campaigns campaigns={ongoingCampaigns} columns={columns} />
        </>
      )}

      {scheduledCampaigns.length > 0 && (
        <>
          <Typography as="h2" variant="caption">
            <FormattedMessage
              defaultMessage="Scheduled campaigns"
              description="Section: future-dated campaigns not started yet"
            />
          </Typography>
          <Campaigns
            campaigns={scheduledCampaigns}
            columns={columns}
            timeZone={timeZone}
          />
        </>
      )}

      {completedCampaigns.length > 0 && (
        <>
          <Typography as="h2" variant="caption">
            <FormattedMessage
              defaultMessage="Completed campaigns"
              description="Section: finished campaigns for historical reference"
            />
          </Typography>
          <Campaigns campaigns={completedCampaigns} columns={columns} />
        </>
      )}

      {showJobPostingCard && <JobPostingCard />}
    </>
  );
}

/**
 * Wrapped in its own Suspense boundary so its fetches (and its
 * `useOpeningDetails()` call) don't wait on sibling sections
 * (AutomationStatusBanner, Analytics, etc.) in `ChannelsContent`.
 */
export function ChannelsSection() {
  return (
    <Suspense fallback={<ChannelsSection.Skeleton />}>
      <ChannelsSectionContent />
    </Suspense>
  );
}

ChannelsSection.Skeleton = function ChannelsSectionSkeleton() {
  return (
    <>
      <Skeleton className="h-3 w-40 shrink-0" />
      <Campaigns.Skeleton />
      <Skeleton className="h-3 w-40 shrink-0" />
      <Campaigns.Skeleton />
      <Skeleton className="h-3 w-40 shrink-0" />
      <Campaigns.Skeleton />
    </>
  );
};
